import { createSlice } from "@reduxjs/toolkit";
import { v4 as uuid } from "uuid";
import {
  filterProduct,
  findProductType,
  findProductVariation,
} from "../../helpers/product";
import i18n from "../../translations/i18n";
import { message } from "antd";

const initialState = [];

export const cartSlice = createSlice({
  name: "cart",
  initialState,
  reducers: {
    addToCartAction: (state, action) => {
      const product = action.payload;
      const cartItem = filterProduct(state, product);

      if (cartItem === undefined) {
        state.push({
          ...product,
          quantity: product.quantity ? product.quantity : 1,
          cartItemId: uuid(),
        });
      } else {
        return state.map((item) =>
          item.cartItemId === cartItem.cartItemId
            ? {
                ...item,
                quantity: product.quantity
                  ? item.quantity + product.quantity
                  : item.quantity + 1,
              }
            : item,
        );
      }
    },
    addMultipleToCartAction: (state, action) => {
      const products = action.payload;

      products.forEach((product) => {
        const cartItem = filterProduct(state, product);

        if (cartItem === undefined) {
          state.push({
            ...product,
            quantity: product.quantity ? product.quantity : 1,
            cartItemId: uuid(),
          });
        } else {
          const existingItemIndex = state.findIndex(
            (item) => item.cartItemId === cartItem.cartItemId,
          );

          if (existingItemIndex !== -1) {
            state[existingItemIndex].quantity = product.quantity
              ? state[existingItemIndex].quantity + product.quantity
              : state[existingItemIndex].quantity + 1;
          }
        }
      });
    },
    decreaseQuantityAction: (state, action) => {
      const product = action.payload;
      const orderUnit = product.orderUnit || 1;

      if (product.quantity === orderUnit) {
        return state.filter(
          (cartItem) => cartItem.cartItemId !== product.cartItemId,
        );
      } else {
        return state.map((item) =>
          item.cartItemId === product.cartItemId
            ? { ...item, quantity: item.quantity - orderUnit }
            : item,
        );
      }
    },
    deleteFromCartAction: (state, action) => {
      const product = action.payload;
      return state.filter(
        (cartItem) => cartItem.cartItemId !== product.cartItemId,
      );
    },
    deleteAllFromCartAction: () => {
      return [];
    },
    updateCartAction: (state, action) => {
      const products = action.payload;
      return state.reduce(function (updatedCart, cartItem) {
        if (cartItem.isGiftCard) {
          updatedCart.push(cartItem);
          return updatedCart;
        }

        const productFound = products?.find(
          (product) => product.id === cartItem.id,
        );

        if (!!productFound && productFound.stock > 0) {
          const updatedCartItem = {
            ...cartItem,
            name: productFound.name,
            image: productFound.image,
            price: productFound.price,
            discount: productFound.discount,
            taxRate: productFound.taxRate,
            stock: productFound.stock,
            variation: findProductVariation(productFound, cartItem),
            type: findProductType(productFound, cartItem),
          };
          updatedCart.push(updatedCartItem);
        }
        return updatedCart;
      }, []);
    },
  },
});

// Action creators
export const {
  addToCartAction,
  addMultipleToCartAction,
  decreaseQuantityAction,
  deleteFromCartAction,
  deleteAllFromCartAction,
  updateCartAction,
} = cartSlice.actions;

// Thunks
export const addToCart =
  (item, quantityCount, variation, type, omitNotification) => (dispatch) => {
    if (!omitNotification) message.success(i18n.t("added_to_cart"), 1);
    dispatch(
      addToCartAction({
        ...item,
        quantity: quantityCount,
        variation: variation,
        type: type,
      }),
    );
  };

export const addMultipleToCart = (products, omitNotification) => (dispatch) => {
  if (!omitNotification) message.success(i18n.t("added_to_cart"), 1);
  dispatch(addMultipleToCartAction(products));
};

export const decreaseQuantity = (item) => (dispatch) => {
  dispatch(decreaseQuantityAction(item));
};

export const deleteFromCart = (item) => (dispatch) => {
  dispatch(deleteFromCartAction(item));
};

export const deleteAllFromCart = () => (dispatch) => {
  dispatch(deleteAllFromCartAction());
};

export const updateCart = (products) => (dispatch) => {
  dispatch(updateCartAction(products));
};

// Utility function
export const cartItemStock = (item) => {
  return item.stock > 0 ? item.stock : 0;
};

export default cartSlice.reducer;
